// ============================================================================
// SHARED ACCESS-CAPABILITY MAP — CANONICAL COPY
// ----------------------------------------------------------------------------
// This file MUST be byte-identical across the three locations:
//   - infrastructure/lambda/_shared/accessCapabilities.ts  (canonical)
//   - frontend/src/lib/accessCapabilities.ts
//   - mobile/app/src/lib/accessCapabilities.ts
// A CI test (`accessCapabilities.copies.test.ts`) hashes the three copies and
// fails the pipeline if they diverge. Do not edit any copy in place — edit
// the canonical file and re-copy.
//
// This is the single canonical source for the access vocabulary and the two
// shared mappings consumed by BOTH the frontend front door and the backend
// resolver authorization.
//
// CANONICAL VOCABULARY (Meridian Data Model, Requirements 2.10 / 7.1):
//   The canonical access `Role` vocabulary is the TWO values `admin | judge`.
//   `poweruser`, `director`, and `staff` are RETIRED — `director` is a display
//   title for `admin`, titleholder elevation is lifecycle-driven (not a Role),
//   and `staff` has no use case. Cognito enforcement groups reduce to exactly
//   `Admins` + `Judges` via `ROLE_TO_GROUP`; the legacy `PowerUsers` and
//   `Applicants` groups are retired (members remapped — see
//   `remapRetiredGroup`). Capabilities are computed from the active Role set
//   plus lifecycle facets (`capabilitiesFromRoles`). See the "CANONICAL
//   TWO-VALUE ROLE VOCABULARY" section at the bottom of this file.
//
// LEGACY 4-HAT LAYER (DEPRECATED): the `AccessHat` union and its
//   `GROUP_TO_HAT` / `HAT_TO_GROUP` / `deriveRoleSet` / `computeCapabilities` /
//   `CAPABILITY_REQUIRED_GROUPS` helpers below are the prior access-roles-
//   unification 4-hat model. They are retained ONLY as a compatibility layer
//   while the web/mobile route guards and the admin user-management Lambda are
//   migrated to the two-value vocabulary (Meridian Data Model Phase 6 / Phase 8
//   — tasks 12.3 and 14). Do not build new consumers on the 4-hat layer.
// ============================================================================

/**
 * @deprecated Legacy 4-hat access vocabulary (access-roles-unification). The
 * canonical vocabulary is the two-value `Role` (`admin | judge`) defined in the
 * "CANONICAL TWO-VALUE ROLE VOCABULARY" section below. Removed in Phase 6/8.
 */
export type AccessHat = 'admin' | 'poweruser' | 'director' | 'judge';

/** Named, surface-agnostic permissions (Requirement glossary / Req 4). */
export type Capability =
  | 'canManageUsers'
  | 'canViewAdminDashboard'
  | 'canJudge'
  | 'canGrantRoles'
  | 'canSeePortal';

/** Cognito group names that are the single Access_Authority. */
export type AccessGroup = 'Admins' | 'PowerUsers' | 'Judges' | 'Applicants';

/** The full Role_Set a user holds plus the portal flag. */
export interface AccessState {
  roleSet: ReadonlySet<AccessHat>;
  portalAccess: boolean;
}

/** Groups → hats. Applicants is NOT a hat (Req 3.2, 3.3). No Directors group. */
export const GROUP_TO_HAT: Readonly<Record<string, AccessHat | undefined>> = {
  Admins: 'admin',
  PowerUsers: 'poweruser',
  Judges: 'judge',
  // Applicants intentionally absent — it confers Portal_Access, not a hat.
};

/** A hat → the Cognito group that confers it (director folds into Admins). */
export const HAT_TO_GROUP: Readonly<Record<AccessHat, AccessGroup>> = {
  admin: 'Admins',
  director: 'Admins', // Requirement 9.1
  poweruser: 'PowerUsers',
  judge: 'Judges',
};

/**
 * Derive the multi-hat Role_Set + Portal_Access from raw Cognito groups.
 * Unknown group names are ignored; duplicates/order irrelevant (Req 3.1–3.5).
 */
export function deriveRoleSet(groups: readonly string[]): AccessState {
  const roleSet = new Set<AccessHat>();
  for (const g of groups) {
    const hat = GROUP_TO_HAT[g];
    if (hat) roleSet.add(hat);
  }
  return { roleSet, portalAccess: groups.includes('Applicants') };
}

/**
 * The single source of truth: which hats independently confer each capability.
 * Each hat in the list grants the capability ON ITS OWN — no capability is
 * synthesized by combining hats (Req 4.6, 5.5, 5.6). `director` always appears
 * wherever `admin` does (Req 4.6, 9.2).
 */
export const CAPABILITY_GRANTING_HATS: Readonly<
  Record<Exclude<Capability, 'canSeePortal'>, readonly AccessHat[]>
> = {
  canManageUsers: ['admin', 'director'],
  canViewAdminDashboard: ['admin', 'director', 'poweruser'],
  canJudge: ['admin', 'director', 'poweruser', 'judge'],
  canGrantRoles: ['admin', 'director', 'poweruser'],
};

/**
 * The Capability_Map: a PURE, deterministic function of Role_Set + Portal_Access
 * only (Req 4.1, 4.8). Never reads User.role, Lifecycle_Stage, or Division.
 * canSeePortal depends solely on Portal_Access (Req 4.7).
 */
export function computeCapabilities(state: AccessState): ReadonlySet<Capability> {
  const caps = new Set<Capability>();
  for (const cap of Object.keys(CAPABILITY_GRANTING_HATS) as (keyof typeof CAPABILITY_GRANTING_HATS)[]) {
    if (CAPABILITY_GRANTING_HATS[cap].some((h) => state.roleSet.has(h))) caps.add(cap);
  }
  if (state.portalAccess) caps.add('canSeePortal');
  return caps;
}

/** Capability → required Cognito groups. Used by BOTH layers (Req 2.4, 15.4). */
export const CAPABILITY_REQUIRED_GROUPS: Readonly<Record<Capability, readonly AccessGroup[]>> = {
  canManageUsers: ['Admins'],
  canViewAdminDashboard: ['Admins', 'PowerUsers'],
  canJudge: ['Admins', 'PowerUsers', 'Judges'],
  canGrantRoles: ['Admins', 'PowerUsers'],
  canSeePortal: ['Applicants'],
};

/** Backward-compat / display-cache priority (Req 11.2, 13.2). */
const ROLE_PRIORITY: readonly AccessHat[] = ['admin', 'poweruser', 'judge'];
export function highestStandingRole(roleSet: ReadonlySet<AccessHat>): AccessHat | 'contestant' {
  for (const h of ROLE_PRIORITY) if (roleSet.has(h)) return h;
  return 'contestant'; // director already normalized to admin in the set
}

// ============================================================================
// CANONICAL TWO-VALUE ROLE VOCABULARY (Meridian Data Model — Req 2.10, 7.1)
// ----------------------------------------------------------------------------
// The single source of truth for the access vocabulary. A `Role` is an access
// "hat" held by a Person, drawn from exactly TWO values: `admin | judge`.
//
//   - `admin`  — org-wide elevation. "Director" is a DISPLAY TITLE for `admin`,
//                not a separate Role. (`poweruser`/`director`/`staff` retired.)
//   - `judge`  — competition-scoped judging access.
//
// `applicant` / `contestant` / `titleholder` / `alumna` are Lifecycle_Stages,
// NEVER Roles. Titleholder elevation (Envoy/Spotlight/Vault) is derived from
// the lifecycle facet through the persona-permission matrix, not from a Role.
//
// Cognito groups are a DERIVED PROJECTION of the active Role set, never an
// independent authority. The enforcement groups reduce to exactly two —
// `Admins` and `Judges` — via `ROLE_TO_GROUP`. The legacy `PowerUsers` and
// `Applicants` groups are retired; existing members are remapped through
// `remapRetiredGroup` (titleholders → lifecycleStage `titleholder`; other
// elevated users → `admin`; applicants → lifecycleStage `applicant`).
// ============================================================================

/** The canonical access Role vocabulary — exactly two values (Req 2.10, 7.1). */
export type Role = 'admin' | 'judge';

/** The two Cognito enforcement groups the Role vocabulary projects to. */
export type EnforcementGroup = 'Admins' | 'Judges';

/** Lifecycle facets (never Roles) used to compute lifecycle-driven access. */
export type LifecycleFacet = 'applicant' | 'contestant' | 'titleholder' | 'alumna';

/** Stable canonical Role ordering for deterministic, order-independent output. */
export const ROLE_ORDER: readonly Role[] = ['admin', 'judge'] as const;

/**
 * Role → Cognito enforcement group projection (the ONLY two groups).
 * `admin → Admins`, `judge → Judges`. This is the canonical projection the
 * single write path (`applyRoleAssignment`, task 8.2) and the drift reconciler
 * (task 8.5) use; it replaces the legacy `HAT_TO_GROUP`.
 */
export const ROLE_TO_GROUP: Readonly<Record<Role, EnforcementGroup>> = {
  admin: 'Admins',
  judge: 'Judges',
};

/**
 * Cognito enforcement group → Role. Only `Admins` and `Judges` map to a Role;
 * the retired `PowerUsers`/`Applicants` groups intentionally do NOT appear here
 * (they are handled by `remapRetiredGroup` during the group-retirement cutover).
 */
export const GROUP_TO_ROLE: Readonly<Record<string, Role | undefined>> = {
  Admins: 'admin',
  Judges: 'judge',
};

/** The complete, post-retirement set of Cognito enforcement groups. */
export const ENFORCEMENT_GROUPS: readonly EnforcementGroup[] = ['Admins', 'Judges'] as const;

/** Cognito groups retired by this vocabulary unification (members remapped). */
export const RETIRED_GROUPS = ['PowerUsers', 'Applicants'] as const;
export type RetiredGroup = (typeof RETIRED_GROUPS)[number];

/** Type guard for the canonical two-value Role vocabulary. */
export function isRole(value: string): value is Role {
  return (ROLE_ORDER as readonly string[]).includes(value);
}

/**
 * The effective Role set for a Person, derived from the role strings on their
 * active RoleAssignment rows (the SSOT). Non-Role strings (e.g. retired
 * `poweruser`) are ignored; the result is de-duplicated and returned in the
 * canonical `ROLE_ORDER`, so it is order- and multiplicity-independent.
 */
export function effectiveRoles(activeRoles: readonly string[]): Role[] {
  const matched = new Set<Role>();
  for (const r of activeRoles) {
    if (isRole(r)) matched.add(r);
  }
  return ROLE_ORDER.filter((r) => matched.has(r));
}

// ----------------------------------------------------------------------------
// Group-derivation equivalents for the importers migrating off the retired
// `roleDerivation.ts` (access-anchor-retirement Workstream B.3.2, R7.1).
// These operate on the two-value vocabulary. The retired `PowerUsers` group no
// longer derives a Role (it is remapped at the seam — see `remapRetiredGroup`),
// so `isElevated` is now exactly "holds the `admin` Role".
// ----------------------------------------------------------------------------

/**
 * Derive the canonical two-value Role set from raw Cognito group names.
 *
 * Only the post-retirement enforcement groups derive a Role (`Admins → admin`,
 * `Judges → judge`) through `GROUP_TO_ROLE`. Unknown / retired group names are
 * ignored; the result is de-duplicated and returned in the canonical
 * `ROLE_ORDER`, so it is order- and multiplicity-independent.
 */
export function deriveRoles(groups: readonly string[]): Role[] {
  const matched = new Set<Role>();
  for (const g of groups) {
    const r = GROUP_TO_ROLE[g];
    if (r) matched.add(r);
  }
  return ROLE_ORDER.filter((r) => matched.has(r));
}

/**
 * Elevation = holding the `admin` Role (drives routing). Under the two-value
 * model the retired `poweruser` elevation folds into `admin` at the remap seam,
 * so an elevated principal still holds `admin` and this returns the same answer
 * for every migrated principal.
 */
export function isElevated(groups: readonly string[]): boolean {
  return deriveRoles(groups).includes('admin');
}

/** True iff the `admin` Role is held. */
export function isAdmin(groups: readonly string[]): boolean {
  return deriveRoles(groups).includes('admin');
}

/**
 * Project an active Role set to the Cognito enforcement group set through
 * `ROLE_TO_GROUP`. De-duplicated and order-independent. This is the `project`
 * used by the single write path and the Andrea single-owner invariant
 * (`project(activeRoles(person)) == cognitoGroups(person)`, task 8.4).
 */
export function projectRolesToGroups(roles: readonly Role[]): EnforcementGroup[] {
  const groups = new Set<EnforcementGroup>();
  for (const r of roles) groups.add(ROLE_TO_GROUP[r]);
  return ENFORCEMENT_GROUPS.filter((g) => groups.has(g));
}

/**
 * Compute the named capabilities a Person holds from their active Role set plus
 * their lifecycle facets. Capabilities are a pure function of (roles, facets):
 *
 *   - `admin` confers canManageUsers, canViewAdminDashboard, canJudge,
 *     canGrantRoles.
 *   - `judge` confers canJudge.
 *   - holding any lifecycle facet (the contestant journey) confers canSeePortal
 *     — this replaces the retired `Applicants`-group-derived Portal_Access.
 *
 * Never reads `User.role`, Division, or Cognito groups directly.
 */
export function capabilitiesFromRoles(
  roles: readonly Role[],
  facets: readonly LifecycleFacet[] = [],
): ReadonlySet<Capability> {
  const caps = new Set<Capability>();
  const has = (r: Role) => roles.includes(r);
  if (has('admin')) {
    caps.add('canManageUsers');
    caps.add('canViewAdminDashboard');
    caps.add('canJudge');
    caps.add('canGrantRoles');
  }
  if (has('judge')) {
    caps.add('canJudge');
  }
  if (facets.length > 0) {
    caps.add('canSeePortal');
  }
  return caps;
}

/** The lifecycle/role outcome of remapping a member of a retired Cognito group. */
export interface RetiredGroupRemap {
  /** A Role to grant via a RoleAssignment row (when the member is elevated). */
  role?: Role;
  /** A lifecycle facet to set on the member's Participation (no Role granted). */
  lifecycleStage?: LifecycleFacet;
}

/**
 * Remap a member of a retired Cognito group to the two-value model
 * (Req 2.10, 7.1). Used by the group-retirement cutover / drift reconciler:
 *
 *   - `Applicants`  → lifecycleStage `applicant` (no Role).
 *   - `PowerUsers`  + titleholder → lifecycleStage `titleholder` (no Role).
 *   - `PowerUsers`  otherwise (elevated) → `admin` Role.
 *
 * Titleholder elevation is lifecycle-driven, so a titleholder PowerUser is
 * remapped to a lifecycle facet rather than an `admin` grant.
 */
export function remapRetiredGroup(
  group: RetiredGroup,
  opts: { isTitleholder?: boolean } = {},
): RetiredGroupRemap {
  if (group === 'Applicants') {
    return { lifecycleStage: 'applicant' };
  }
  // PowerUsers
  if (opts.isTitleholder) {
    return { lifecycleStage: 'titleholder' };
  }
  return { role: 'admin' };
}

// ============================================================================
// LIFECYCLE-CLAIM VOCABULARY (durable-portal-access — Req 4.1, 4.2, 4.3)
// ----------------------------------------------------------------------------
// The typed parse/serialize/type-guard helpers for the `lifecycle` ID-token
// claim. Both the frontend `AuthContext` and the Pre-Token-Generation Lambda
// derive facets through these SAME helpers so facet-derived and legacy
// group-derived `canSeePortal` agree for the transition population. Parsing is
// strict/typed (mirrors `isRole`/`isProduct`): unknown/mixed-case tokens are
// dropped, and the result is de-duplicated and returned in the canonical
// `LIFECYCLE_FACET_ORDER`, so it is order- and multiplicity-independent.
// ============================================================================

/**
 * Stable canonical LifecycleFacet ordering for deterministic, order-independent
 * output. Used by BOTH `parseLifecycleFacets` and `serializeLifecycleFacets` so
 * their round-trip is well-defined (mirrors `ROLE_ORDER` / `PERSONA_ORDER`).
 */
export const LIFECYCLE_FACET_ORDER: readonly LifecycleFacet[] = [
  'applicant',
  'contestant',
  'titleholder',
  'alumna',
] as const;

/** Type guard for the four LifecycleFacet values (mirrors `isRole`/`isProduct`). */
export function isLifecycleFacet(value: string): value is LifecycleFacet {
  return (LIFECYCLE_FACET_ORDER as readonly string[]).includes(value);
}

/**
 * The delimiter set the claim tolerates when parsing a delimited string form:
 * commas and/or any run of whitespace. `serializeLifecycleFacets` always emits
 * the comma form, but parse is defensive about either.
 */
const LIFECYCLE_CLAIM_DELIMITER = /[\s,]+/;

/**
 * Parse a `lifecycle` claim value into a de-duplicated, canonically ordered
 * subset of the four recognized facets. Accepts a single value, a delimited
 * string (comma- and/or whitespace-separated), or an array of tokens; any other
 * input (`undefined`/`null`/number/object) yields `[]`. Unrecognized or
 * mixed-case tokens are dropped under the file's strict-equality conventions.
 * NEVER throws.
 */
export function parseLifecycleFacets(claim: unknown): LifecycleFacet[] {
  const matched = new Set<LifecycleFacet>();
  const consider = (token: unknown): void => {
    if (typeof token === 'string' && isLifecycleFacet(token)) {
      matched.add(token);
    }
  };
  if (typeof claim === 'string') {
    for (const token of claim.split(LIFECYCLE_CLAIM_DELIMITER)) consider(token);
  } else if (Array.isArray(claim)) {
    for (const token of claim) consider(token);
  }
  // Any other input shape falls through with an empty match set.
  return LIFECYCLE_FACET_ORDER.filter((f) => matched.has(f));
}

/**
 * The deterministic canonical string form of a facet set, round-trip compatible
 * with `parseLifecycleFacets`: it de-duplicates, orders by
 * `LIFECYCLE_FACET_ORDER`, and joins with commas. This is the form the
 * Pre-Token-Generation trigger writes into the claim and the write path derives
 * from a Participation `lifecycleStage`.
 */
export function serializeLifecycleFacets(facets: readonly LifecycleFacet[]): string {
  const present = new Set<LifecycleFacet>();
  for (const f of facets) {
    if (isLifecycleFacet(f)) present.add(f);
  }
  return LIFECYCLE_FACET_ORDER.filter((f) => present.has(f)).join(',');
}

/**
 * The safe-default facet set the Pre-Token-Generation trigger emits on any
 * failure (missing/empty/malformed attribute or a thrown error). Non-empty so a
 * confirmed contestant is never locked out, while granting nothing beyond
 * portal visibility — `applicant` confers only `canSeePortal`.
 */
export const SAFE_DEFAULT_LIFECYCLE_FACETS: readonly LifecycleFacet[] = ['applicant'] as const;

// ============================================================================
// ORG PRODUCT SUBSCRIPTION × PERSONA-PERMISSION MATRIX (Meridian Data Model —
// Req 9.1–9.5, 8.4, 8.6)
// ----------------------------------------------------------------------------
// Product access is the composition of TWO orthogonal axes ANDed together:
//
//   1. Product availability  — an org-level, ALL-OR-NOTHING subscription flag
//      per Product (`OrgProductSubscriptions`). There is NO per-user payment;
//      subscribing turns a Product on for the whole org.
//   2. Persona permission    — a DECLARATIVE matrix (`PERSONA_PERMISSION_MATRIX`)
//      of which Persona (derived from lifecycle facets + roles) may use which
//      Product, with a per-(product, persona) Row_Scope.
//
//   >>> Effective product access = (org subscribed to product)
//                                   AND (persona permitted by the matrix). <<<
//
// The matrix is stored as DATA evaluated by ONE shared evaluator
// (`hasProductAccess` / `evaluateProductAccess`) — never as scattered
// `if (role === …)` checks (the "five meanings" fragmentation this redesign
// removes). This is the single source web + mobile + backend all consume; it is
// byte-identical across the three accessCapabilities copies.
// ============================================================================

/**
 * The subscribable application surfaces (Req 9.4). Exactly SEVEN products.
 * `Reign` is Podium's mobile delivery channel and the `Judge PWA` is Merit's
 * judging channel — neither is a separate subscription/permission.
 */
export type Product =
  | 'Podium'
  | 'Director'
  | 'Merit'
  | 'Envoy'
  | 'Patron'
  | 'Spotlight'
  | 'Vault';

/** Stable canonical Product ordering for deterministic, order-independent output. */
export const PRODUCT_CATALOG: readonly Product[] = [
  'Podium',
  'Director',
  'Merit',
  'Envoy',
  'Patron',
  'Spotlight',
  'Vault',
] as const;

/** Type guard for the Product catalog. */
export function isProduct(value: string): value is Product {
  return (PRODUCT_CATALOG as readonly string[]).includes(value);
}

/**
 * The access identity a product-access decision is evaluated against, derived
 * from a Person's lifecycle facets plus roles. `applicant`/`contestant`/
 * `titleholder`/`alumna` come from the contestant facet; `admin`/`judge` from
 * the active Role set; `sponsor` is a contact facet (Patron permission), NEVER
 * a Role (Req 11.5).
 */
export type Persona =
  | 'applicant'
  | 'contestant'
  | 'titleholder'
  | 'alumna'
  | 'admin'
  | 'judge'
  | 'sponsor';

/** Stable canonical Persona ordering for deterministic, order-independent output. */
export const PERSONA_ORDER: readonly Persona[] = [
  'admin',
  'judge',
  'sponsor',
  'titleholder',
  'contestant',
  'applicant',
  'alumna',
] as const;

/**
 * The per-viewer row filter layered on top of product access (Req 9.6):
 *   - `aggregate` — the viewer (admin) sees the whole set.
 *   - `own`       — the viewer sees only their own rows (and, for a judge in
 *                   Merit, only the competition(s) they actively judge — Req 9.5).
 *   - `shared`    — a shared social surface with no self/aggregate split (Podium).
 */
export type RowScope = 'aggregate' | 'own' | 'shared';

/**
 * Org-level, all-or-nothing Product_Subscription flags (Req 9.2). One flag per
 * Product; `true` makes the Product available to the entire org. This is the
 * org/tenant subscription state the evaluator reads as Axis 1.
 */
export type OrgProductSubscriptions = Readonly<Record<Product, boolean>>;

/** Convenience: the all-off subscription state (nothing subscribed). */
export const NO_PRODUCT_SUBSCRIPTIONS: OrgProductSubscriptions = {
  Podium: false,
  Director: false,
  Merit: false,
  Envoy: false,
  Patron: false,
  Spotlight: false,
  Vault: false,
} as const;

/** One declarative permission entry: a permitted persona and its row scope. */
export interface PersonaPermission {
  readonly persona: Persona;
  readonly scope: RowScope;
}

/**
 * The Persona_Permission_Matrix (Req 9.3, 9.4) — declarative DATA, evaluated by
 * the single shared evaluator below. Encodes R9.4 exactly:
 *
 *   - Podium     — broad: applicant, contestant, titleholder, alumna, admin
 *                  (shared social surface; alumna retains Podium — Req 8.4).
 *   - Director   — admin (full / aggregate); applicant + contestant (their own
 *                  application surface only).
 *   - Merit      — admin (configure / aggregate); judge (Judge PWA, own scope —
 *                  per-competition, Req 9.5). Contestants & titleholders are
 *                  EXCLUDED (absent from this list — Req 9.5).
 *   - Envoy      — admin (aggregate); titleholder (own commitments).
 *   - Patron     — admin (all sponsors); sponsor (own agreement only — Req 11.5).
 *   - Spotlight  — admin (aggregate); titleholder (own metrics).
 *   - Vault      — admin (aggregate); titleholder (own expenses).
 *
 * A persona absent from a product's list is NOT permitted that product.
 */
export const PERSONA_PERMISSION_MATRIX: Readonly<
  Record<Product, readonly PersonaPermission[]>
> = {
  Podium: [
    { persona: 'applicant', scope: 'shared' },
    { persona: 'contestant', scope: 'shared' },
    { persona: 'titleholder', scope: 'shared' },
    { persona: 'alumna', scope: 'shared' },
    { persona: 'admin', scope: 'shared' },
  ],
  Director: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'applicant', scope: 'own' },
    { persona: 'contestant', scope: 'own' },
  ],
  Merit: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'judge', scope: 'own' },
  ],
  Envoy: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'titleholder', scope: 'own' },
  ],
  Patron: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'sponsor', scope: 'own' },
  ],
  Spotlight: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'titleholder', scope: 'own' },
  ],
  Vault: [
    { persona: 'admin', scope: 'aggregate' },
    { persona: 'titleholder', scope: 'own' },
  ],
} as const;

/**
 * Derive the set of Personas a Person presents, from their active Role set, their
 * lifecycle facets, and whether they are a sponsor contact. De-duplicated and
 * returned in the canonical `PERSONA_ORDER` so it is order- and
 * multiplicity-independent. A Person MAY present more than one Persona at once
 * (e.g. an alumna admin → `['admin','alumna']`).
 */
export function derivePersonas(
  roles: readonly Role[],
  facets: readonly LifecycleFacet[] = [],
  opts: { isSponsor?: boolean } = {},
): Persona[] {
  const matched = new Set<Persona>();
  for (const r of roles) {
    if (r === 'admin') matched.add('admin');
    else if (r === 'judge') matched.add('judge');
  }
  for (const f of facets) {
    // LifecycleFacet values are a subset of Persona values.
    matched.add(f);
  }
  if (opts.isSponsor) matched.add('sponsor');
  return PERSONA_ORDER.filter((p) => matched.has(p));
}

/** Whether a single Persona is permitted a Product by the matrix (Axis 2 only). */
export function isPersonaPermitted(product: Product, persona: Persona): boolean {
  return PERSONA_PERMISSION_MATRIX[product].some((e) => e.persona === persona);
}

/**
 * The Row_Scope a Persona gets for a Product, or `undefined` if the Persona is
 * not permitted that Product (Req 9.6).
 */
export function personaScope(product: Product, persona: Persona): RowScope | undefined {
  return PERSONA_PERMISSION_MATRIX[product].find((e) => e.persona === persona)?.scope;
}

// ============================================================================
// TENANT + CONSENT ENFORCEMENT AXES (tenant-foundation — Req 3.3, 3.5, 7.3, 7.6)
// ----------------------------------------------------------------------------
// Two ADDITIONAL orthogonal AND-axes layered on top of the (subscription ×
// persona) product-access decision, both LIVE since the Task 10 enforcement flip:
//
//   3. Tenant axis (Req 3.3, 3.5): a product-access decision requires an ACTIVE
//      tenant resolved from the caller's token (`custom:activeTenantId`) AND a
//      validated Membership in that Tenant. The client SELECTS a Tenant but
//      never ASSERTS it — tenantId comes from the token, never a client argument
//      (Req 3.2); a requested Tenant with no matching Membership is refused.
//   4. Consent axis (Req 7.3, 7.6): for a Child_Account, product access that
//      would collect or expose the minor's personal data is denied unless the
//      per-(child, Tenant) Membership consent state is GRANTED (fail closed on
//      PENDING / DENIED / REVOKED / absent).
//
// DARK-LAUNCH HISTORY: these axes shipped behind `TENANCY_ENFORCEMENT_ENABLED`,
// which was `false` at first (Task 4), so `tenantConsentGate` returned `true`
// unconditionally and `hasProductAccess` / `evaluateProductAccess` made EXACTLY
// the pre-tenancy (subscription × persona) decision. Task 10 FLIPPED the flag to
// `true` (after Idaho adoption in Task 8 and after callers thread
// `TenantAccessContext`), so both axes are now LIVE. The flag is a shared
// compile-time constant, so all three byte-identical copies enforce together.
// ============================================================================

/** Per-(child, Tenant) Membership consent state (tenant-foundation Req 7, 17.6). */
export type ConsentState = 'PENDING' | 'GRANTED' | 'DENIED' | 'REVOKED';

/**
 * The Tenant's own lifecycle status (tenant-foundation Req 15.7) — mirrors the
 * GraphQL `TenantStatus` enum and `TENANT_STATUSES` in the tenant-service. Kept
 * here because the shared evaluator gates on it, and this module may not import
 * from the tenant-service (it is copied verbatim into the web + mobile clients).
 */
export type TenantLifecycleStatus = 'active' | 'suspended';

/**
 * An ALREADY-VALIDATED platform-operator support grant (tenant-foundation Task 7.1 /
 * Req 15.6) — a narrow, expiring, read-only window into one Tenant for an operator
 * who is NOT a member of it.
 *
 * WHY IT IS "ALREADY VALIDATED". Expiry is a clock question and this module is pure
 * and clock-free (it is copied verbatim into the web and mobile clients, where a
 * client clock must never be authoritative). So the caller — the backend resolver,
 * which has both a clock and the durable record — decides whether a grant is live
 * via `evaluateSupportGrant`, and only a grant that PASSED gets put in this context.
 * The presence of this object therefore means "a live grant was proven server-side",
 * never "a grant was claimed".
 *
 * The `reason` travels with it deliberately: an operator looking at a tenant's data
 * with no recorded reason is the thing an audit is supposed to make impossible, so
 * the reason is part of what makes the grant valid rather than metadata beside it.
 */
export interface SupportAccessContext {
  /** The Tenant this grant authorizes. MUST equal the tenant being viewed. */
  readonly tenantId: string;
  /** When the grant lapses (ISO 8601) — carried for display/audit, not re-checked here. */
  readonly expiresAt: string;
  /** The operator's recorded justification. A blank reason invalidates the grant. */
  readonly reason: string;
}

/**
 * The tenant + membership + consent context a product-access decision is
 * evaluated against. Resolved from the caller's AUTHENTICATED identity — the
 * active `tenantId` claim plus the validated Membership (and, for a minor, the
 * membership consent state) — NEVER from a client-supplied argument (Req 3.2).
 * Every field is optional so the type composes into the existing evaluator
 * signatures without forcing callers to supply it during the dark phase.
 */
export interface TenantAccessContext {
  /** The active tenant resolved from the token claim (Req 3.1). Absent → unresolved. */
  readonly activeTenantId?: string;
  /**
   * Whether the caller holds a VALIDATED Membership in `activeTenantId`
   * (Req 3.3, 3.5) — this validation is the isolation enforcement point.
   */
  readonly hasActiveMembership?: boolean;
  /**
   * The active Tenant's own lifecycle status (tenant-foundation Task 18 / Req 15.7).
   * A `suspended` Tenant is an operator hold on the WHOLE organization, which is a
   * different axis from the caller's individual standing: every member of a
   * suspended Tenant is held, including its admins.
   *
   * Absent means "not asserted", not "active" — see `tenantConsentGate` for why an
   * absent status does not itself deny.
   */
  readonly tenantStatus?: TenantLifecycleStatus;
  /** Whether the data subject is a Child_Account (consent gating applies only to minors). */
  readonly isChildAccount?: boolean;
  /** For a Child_Account, the per-(child, Tenant) Membership consent state (Req 7.3, 7.6). */
  readonly consentState?: ConsentState;
  /**
   * A server-validated platform-operator support grant (Task 7.1 / Req 15.6).
   *
   * DELIBERATELY IGNORED BY `tenantConsentGate` AND THEREFORE BY
   * `hasProductAccess` / `evaluateProductAccess`. Support access is granted ONLY
   * through the separate `hasSupportReadAccess` entry point. See that function for
   * why the separation — rather than widening the main gate — is the whole safety
   * property.
   */
  readonly supportAccess?: SupportAccessContext;
}

/**
 * The tenant + consent enforcement flag. Shipped `false` at Task 4 (dark launch)
 * so the axes were INERT (no behavior change; the subscription × persona decision
 * stood); FLIPPED to `true` at Task 10 (after Idaho adoption in Task 8 and after
 * callers thread `TenantAccessContext`), so both axes are now LIVE. Typed
 * `boolean` (not the literal `true`) so the historical inert branch below is not
 * seen as dead code.
 */
export const TENANCY_ENFORCEMENT_ENABLED: boolean = true;

/**
 * The tenant + consent AND-gate (Req 3.3, 3.5, 7.3, 7.6) — `true` iff the two
 * new axes permit access. Composed as `base AND tenantConsentGate(ctx)` inside
 * the shared evaluators.
 *
 *   - Historically DARK: while `TENANCY_ENFORCEMENT_ENABLED` was `false` it
 *     returned `true` (inert). The flag is now `true`, so the gate is LIVE.
 *   - Tenant axis: once enabled, deny (fail closed) when no active tenant is
 *     resolved or the caller holds no validated Membership in it (Req 3.5).
 *   - Tenant LIFECYCLE axis: deny when the active Tenant is `suspended`
 *     (Req 15.7). An operator hold applies to the whole organization, so it must
 *     outrank the caller's individual standing — otherwise suspending a tenant
 *     would leave its admins (the people most able to keep operating) unaffected.
 *   - Consent axis: once enabled, for a Child_Account deny unless consent is
 *     `GRANTED` (fail closed on any other state — Req 7.6).
 *
 * WHY AN ABSENT `tenantStatus` DOES NOT DENY. Every field here is optional so the
 * context can be threaded through callers incrementally, and this gate follows the
 * same shape it already uses for `isChildAccount`: the clause fires only on an
 * explicit adverse value. Denying on absence would not make anyone safer — the
 * membership axis above has already established that the caller is a validated
 * active member — while it WOULD deny every caller not yet threaded, which is an
 * outage rather than a safety gain. The backend populator
 * (`resolveTenantAccessContext`) therefore REQUIRES a status reader, so on the
 * server the value is never absent in practice.
 *
 * Pure and deterministic given the flag; never reads env or performs I/O.
 */
export function tenantConsentGate(ctx?: TenantAccessContext): boolean {
  if (!TENANCY_ENFORCEMENT_ENABLED) return true; // historical dark-launch escape (flag now true)
  if (!ctx) return false; // enforcement on, no context resolved → fail closed
  if (!ctx.activeTenantId || ctx.hasActiveMembership !== true) return false;
  if (ctx.tenantStatus === 'suspended') return false; // operator hold on the whole tenant
  if (ctx.isChildAccount === true && ctx.consentState !== 'GRANTED') return false;
  return true;
}

/**
 * THE single shared evaluator (Req 9.1) — the AND of the axes:
 *
 *   effective access = (org subscribed to product) AND (some persona permitted)
 *                      AND (tenant + consent gate — LIVE since Task 10).
 *
 * If any axis is false, access is denied. No product-access decision may be made
 * outside this function (Req 9.3). The tenant + consent gate (`tenantConsentGate`)
 * went LIVE when `TENANCY_ENFORCEMENT_ENABLED` flipped at Task 10, so it now ANDs
 * the tenant + consent axes onto the (subscription × persona) decision.
 */
export function hasProductAccess(
  product: Product,
  personas: readonly Persona[],
  subscriptions: OrgProductSubscriptions,
  tenantContext?: TenantAccessContext,
): boolean {
  if (!tenantConsentGate(tenantContext)) return false;
  if (subscriptions[product] !== true) return false;
  return personas.some((p) => isPersonaPermitted(product, p));
}

/**
 * Is a server-validated support grant present AND applicable to the tenant being
 * viewed? (tenant-foundation Task 7.1 / Req 15.6.)
 *
 * Three ways this returns false, each closing a distinct hole:
 *   - no grant, or no resolved tenant → nothing to authorize.
 *   - the grant names a DIFFERENT tenant than the one being viewed → a grant for
 *     tenant A must never open tenant B. This is the check that stops a single
 *     support grant becoming a cross-tenant skeleton key.
 *   - a blank reason → an unjustified look at someone's data is precisely what the
 *     audit trail exists to prevent, so the reason is load-bearing rather than
 *     decorative.
 *
 * Pure; expiry was established server-side (see `SupportAccessContext`).
 */
export function isSupportAccessActive(ctx?: TenantAccessContext): boolean {
  const grant = ctx?.supportAccess;
  if (!grant) return false;
  const viewing = ctx?.activeTenantId?.trim();
  if (!viewing) return false;
  if (grant.tenantId?.trim() !== viewing) return false;
  if (typeof grant.reason !== 'string' || grant.reason.trim().length === 0) return false;
  return true;
}

/**
 * READ-ONLY product access for a platform operator holding a support grant
 * (Req 15.6). This is a SEPARATE entry point, and that separation is the entire
 * safety property — so it is worth stating plainly why:
 *
 * `hasProductAccess` is used for both reads AND writes across this codebase. Had
 * support grants been folded into it (or into `tenantConsentGate` beneath it),
 * every existing write path would have silently started accepting an operator
 * grant, and the read-only promise would depend on each of those call sites
 * remembering to re-check. Failing closed by DISCIPLINE is not failing closed.
 *
 * Instead: support access satisfies nothing in the main gate — an operator with a
 * grant and no Membership is denied by `hasProductAccess` exactly as a stranger is
 * — and only surfaces that deliberately call THIS function can serve them. A write
 * path cannot leak support access by omission, because omission is the safe state.
 *
 * WHAT IT STILL WILL NOT DO:
 *   - It does not bypass COPPA. A Child_Account's data stays hidden unless consent
 *     is `GRANTED`, identically to a member. An operator's convenience is not a
 *     lawful basis for a minor's PII, so this clause is not negotiable.
 *   - It does not ignore the product catalogue: the tenant must still be subscribed
 *     to the product, so support cannot preview something the tenant never bought.
 *
 * WHAT IT DELIBERATELY ALLOWS: a SUSPENDED tenant. Suspension is a hold on that
 * organization's own members; an operator frequently needs to look at a tenant
 * precisely because it is suspended, and blocking that would make the hold
 * self-defeating.
 */
export function hasSupportReadAccess(
  product: Product,
  subscriptions: OrgProductSubscriptions,
  ctx?: TenantAccessContext,
): boolean {
  if (!TENANCY_ENFORCEMENT_ENABLED) return false; // no support path while tenancy is dark
  if (!isSupportAccessActive(ctx)) return false;
  // NO COPPA BYPASS — same clause the member path applies.
  if (ctx?.isChildAccount === true && ctx?.consentState !== 'GRANTED') return false;
  if (subscriptions[product] !== true) return false;
  return true;
}

/**
 * Is this session operating under a support grant rather than a Membership? Surfaces
 * use this to render the unmistakable "you are viewing another organization as an
 * operator" treatment, and write paths can use it as a belt-and-braces refusal on
 * top of the structural guarantee above.
 */
export function isReadOnlySupportSession(ctx?: TenantAccessContext): boolean {
  return isSupportAccessActive(ctx) && ctx?.hasActiveMembership !== true;
}

/**
 * The competition years a judge actively judges, derived from their active
 * `judge` RoleAssignment rows (Req 9.5). A judge's Merit access is scoped to
 * exactly these competition(s). An org-wide (`competitionYear` null/undefined)
 * judge assignment is ignored for Merit competition scoping — judging is always
 * competition-scoped. De-duplicated and ascending.
 */
export function judgeMeritCompetitionYears(
  activeRoleAssignments: readonly { role: string; competitionYear?: number | null }[],
): number[] {
  const years = new Set<number>();
  for (const ra of activeRoleAssignments) {
    if (ra.role === 'judge' && typeof ra.competitionYear === 'number') {
      years.add(ra.competitionYear);
    }
  }
  return [...years].sort((a, b) => a - b);
}

/** The resolved outcome of evaluating one Product for one Person. */
export interface ProductAccessResult {
  /** Effective access = subscribed AND a permitted persona (Req 9.1). */
  readonly granted: boolean;
  /**
   * The most permissive Row_Scope across the Person's permitted personas for the
   * Product (`aggregate` > `own` > `shared`), or `undefined` when not granted.
   */
  readonly scope?: RowScope;
  /**
   * For Merit when access is granted solely via the `judge` persona, the
   * competition years the judge may access (Req 9.5). Empty/undefined otherwise.
   */
  readonly competitionYears?: number[];
}

const SCOPE_RANK: Readonly<Record<RowScope, number>> = {
  aggregate: 2,
  own: 1,
  shared: 0,
};

/**
 * Evaluate one Product for a Person, returning effective access, the resolved
 * Row_Scope, and (for a judge in Merit) the competition scoping (Req 9.1, 9.5,
 * 9.6). Combines both axes through the single matrix evaluator.
 *
 * Merit competition scoping (Req 9.5): a judge is granted Merit ONLY when they
 * hold at least one active competition-scoped `judge` RoleAssignment; access is
 * limited to those competition years. An admin's Merit access (aggregate) is
 * unaffected by competition scoping.
 */
export function evaluateProductAccess(
  product: Product,
  personas: readonly Persona[],
  subscriptions: OrgProductSubscriptions,
  opts: { judgeCompetitionYears?: readonly number[]; tenantContext?: TenantAccessContext } = {},
): ProductAccessResult {
  if (!tenantConsentGate(opts.tenantContext)) return { granted: false };
  if (subscriptions[product] !== true) return { granted: false };

  const permitted = personas.filter((p) => isPersonaPermitted(product, p));
  if (permitted.length === 0) return { granted: false };

  // Merit + judge persona: gate on, and scope to, actively-judged competitions.
  if (product === 'Merit') {
    const isAdmin = permitted.includes('admin');
    const isJudge = permitted.includes('judge');
    const years = [...(opts.judgeCompetitionYears ?? [])];
    if (!isAdmin && isJudge && years.length === 0) {
      // A judge with no active competition assignment gets no Merit access.
      return { granted: false };
    }
    const scope: RowScope = isAdmin ? 'aggregate' : 'own';
    return {
      granted: true,
      scope,
      ...(isJudge && !isAdmin
        ? { competitionYears: [...years].sort((a, b) => a - b) }
        : {}),
    };
  }

  // Resolve the most permissive scope across the permitted personas.
  let best: RowScope = 'shared';
  for (const p of permitted) {
    const s = personaScope(product, p);
    if (s && SCOPE_RANK[s] > SCOPE_RANK[best]) best = s;
  }
  return { granted: true, scope: best };
}
